import { useMap } from '@vis.gl/react-google-maps';
import { memo, useEffect, useRef } from 'react';

import { useTheme } from '@mui/material';

export type LatLng = { lat: number; lng: number };

type MapPolygonProps = {
  paths: LatLng[] | LatLng[][];
  strokeColor?: string;
  fillColor?: string;
  strokeWeight?: number;
  fillOpacity?: number;
  strokeOpacity?: number;
  clickable?: boolean;
  zIndex?: number;
  onClick?: (event: google.maps.MapMouseEvent) => void;
  onMouseEnter?: (event: google.maps.PolyMouseEvent) => void;
  onMouseLeave?: (event: google.maps.PolyMouseEvent) => void;
};

export const MapPolygon = memo((props: MapPolygonProps) => {
  const { palette } = useTheme();
  const map = useMap();
  const polygonRef = useRef<google.maps.Polygon | null>(null);

  const {
    paths,
    onClick,
    onMouseEnter,
    onMouseLeave,
    clickable = false,
    strokeColor = palette.accent_yellow,
    fillColor = palette.accent_text_30,
    strokeWeight = 2,
    fillOpacity = 0.35,
    strokeOpacity = 1,
    zIndex = 1,
  } = props;

  useEffect(() => {
    if (!map || polygonRef.current) return;

    const polygon = new google.maps.Polygon();
    polygon.setMap(map);
    polygonRef.current = polygon;

    return () => {
      polygon.setMap(null);
      polygonRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    if (!polygonRef.current) return;

    polygonRef.current.setOptions({
      paths,
      clickable,
      strokeColor,
      fillColor,
      strokeWeight,
      fillOpacity,
      strokeOpacity,
      zIndex,
    });
  }, [paths, clickable, strokeColor, fillColor, strokeWeight, fillOpacity, strokeOpacity, zIndex]);

  useEffect(() => {
    if (!polygonRef.current || !onClick) return;

    const listener = google.maps.event.addListener(polygonRef.current, 'click', onClick);

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [onClick]);

  useEffect(() => {
    if (!polygonRef.current || !onMouseEnter) return;

    const listener = google.maps.event.addListener(polygonRef.current, 'mouseover', onMouseEnter);

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [onMouseEnter]);

  useEffect(() => {
    if (!polygonRef.current || !onMouseLeave) return;

    const listener = google.maps.event.addListener(polygonRef.current, 'mouseout', onMouseLeave);

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [onMouseLeave]);

  return null;
});
